/**
 * Recovery for a lazily loaded screen whose code file can no longer be fetched.
 *
 * Every screen is its own chunk with a content hash in its name. After a deploy
 * the old hashes are gone from the server, so a tab opened before the deploy
 * fails the moment it navigates to a screen it has not loaded yet. The dev
 * server does the same after re-optimising dependencies or a branch switch.
 * Either way the fix is a fresh page load, which fetches the current manifest.
 *
 * The reload is guarded so a chunk that is genuinely broken (not merely stale)
 * shows the error screen instead of reloading forever. The guard is a single
 * timestamp in `sessionStorage` — not application state and not PII, just
 * enough to survive the reload it triggers.
 */

const GUARD_KEY = 'turon-admin.chunk-reload-at'

/** A second failure inside this window is treated as real, not stale. */
const GUARD_WINDOW_MS = 10_000

const CHUNK_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i

/**
 * Set once this page has started a reload. Every later caller — StrictMode's
 * second effect run, or `vite:preloadError` and an error boundary reacting to
 * the same failed import — must see the reload as in progress, not read the
 * guard it just wrote as "already reloaded" and show the error screen while the
 * page is unloading.
 */
let reloadStarted = false

export function isChunkLoadError(error: unknown): boolean {
  return error instanceof Error && CHUNK_ERROR_PATTERN.test(error.message)
}

/**
 * Reloads the page unless it already reloaded for this reason moments ago.
 * Returns `true` when a reload was started (now or by an earlier caller on this
 * page), so the caller can render a placeholder instead of the error screen.
 */
export function reloadOnceForStaleChunk(): boolean {
  if (reloadStarted) return true

  const now = Date.now()

  try {
    const last = Number(sessionStorage.getItem(GUARD_KEY))
    if (Number.isFinite(last) && now - last < GUARD_WINDOW_MS) return false
    sessionStorage.setItem(GUARD_KEY, String(now))
  } catch {
    // Without storage there is no loop guard, so do not risk a reload loop.
    return false
  }

  reloadStarted = true
  window.location.reload()
  return true
}
